export const PERFECT_TIPS_BASE_STAKE = 100;
export const PERFECT_TIPS_OUTCOMES = ["1", "X", "2"];

const probabilityOutcomes = (distribution) => {
  const keys = Object.keys(distribution || {});
  return keys.length >= 2 ? keys : PERFECT_TIPS_OUTCOMES;
};

const probabilityValues = (distribution, outcomes = probabilityOutcomes(distribution)) =>
  outcomes.map((outcome) => Number(distribution?.[outcome]));

export function isCompleteProbabilityDistribution(distribution) {
  const outcomes = probabilityOutcomes(distribution);
  const values = probabilityValues(distribution, outcomes);
  return (
    values.every((value) => Number.isFinite(value) && value > 0) &&
    Math.abs(values.reduce((sum, value) => sum + value, 0) - 1) <= 0.001
  );
}

export function normalizeImpliedProbabilities(distribution) {
  const outcomes = probabilityOutcomes(distribution);
  const values = probabilityValues(distribution, outcomes);
  if (!values.every((value) => Number.isFinite(value) && value > 0)) {
    return null;
  }
  const sum = values.reduce((total, value) => total + value, 0);
  if (!Number.isFinite(sum) || sum <= 0) return null;
  return Object.fromEntries(
    outcomes.map((outcome, index) => [outcome, values[index] / sum])
  );
}

export function calculatePerfectTipsGame(
  bettyProbabilities,
  comparisonProbabilities,
  realizedOutcome = null,
  baseStake = PERFECT_TIPS_BASE_STAKE
) {
  if (
    !isCompleteProbabilityDistribution(bettyProbabilities) ||
    !isCompleteProbabilityDistribution(comparisonProbabilities) ||
    !Number.isFinite(Number(baseStake)) ||
    Number(baseStake) <= 0
  ) {
    return null;
  }

  const outcomesList = probabilityOutcomes(bettyProbabilities);
  if (
    outcomesList.some((outcome) => !Object.prototype.hasOwnProperty.call(comparisonProbabilities, outcome)) ||
    Object.keys(comparisonProbabilities || {}).length !== outcomesList.length
  ) return null;

  const outcomes = Object.fromEntries(
    outcomesList.map((outcome) => {
      const probabilityA = Number(bettyProbabilities[outcome]);
      const probabilityB = Number(comparisonProbabilities[outcome]);
      const stake = (probabilityA + probabilityB) * Number(baseStake);
      return [outcome, {
        probabilityA,
        probabilityB,
        stake,
        value: stake * (1 / probabilityB - 1 / probabilityA),
      }];
    })
  );
  const totalStake = outcomesList.reduce(
    (sum, outcome) => sum + outcomes[outcome].stake,
    0
  );

  return {
    outcomes,
    totalStake,
    realizedOutcome: outcomesList.includes(realizedOutcome)
      ? realizedOutcome
      : null,
    realizedValue: outcomesList.includes(realizedOutcome)
      ? outcomes[realizedOutcome].value
      : null,
  };
}

export function summarizePerfectTipsGames(results) {
  const realized = (Array.isArray(results) ? results : [])
    .map((result) => result?.realizedValue)
    .filter(Number.isFinite);
  const totalValue = realized.reduce((sum, value) => sum + value, 0);
  return {
    fixtures: realized.length,
    totalValue,
    valuePerFixture: realized.length ? totalValue / realized.length : null,
    positiveFixtures: realized.filter((value) => value > 0).length,
    negativeFixtures: realized.filter((value) => value < 0).length,
  };
}
